import { ClaimMethod, ClaimRequestStatus } from '@up2stay/up2promo-api-client';

/** A product was scanned. Mirrors the backend's `ProductScannedRealtimeEvent`. */
export type ProductScannedRealtimeEvent = {
  v: 1;
  type: 'product.scanned';
  workspaceId: string;
  productId: string;
  hash: string;
  locationId?: string | null;
  behavior?: string | null;
  label?: string | null;
  scannedAt: string;
};

/** Fired on every (bot-filtered) profile view. Mirrors the backend's `ProfileViewedRealtimeEvent`. */
export type ProfileViewedRealtimeEvent = {
  v: 1;
  type: 'profile.viewed';
  workspaceId: string;
  profileId: string;
  userId?: string | null;
  hash?: string | null;
  source: string;
  viewedAt: string;
};

export type ReviewSyncRealtimeEvent = {
  v: 1;
  type: 'review_sync.started' | 'review_sync.completed' | 'review_sync.failed';
  workspaceId: string;
  locationId: string;
  provider: string;
  status?: 'unlinked';
  occurredAt: string;
};

export type ReviewChangedRealtimeEvent = {
  v: 1;
  type: 'review.created' | 'review.updated';
  workspaceId: string;
  locationId: string;
  provider: string;
  reviewId: string;
  providerReviewId: string;
  occurredAt: string;
};

export type NotificationCreatedRealtimeEvent = {
  v: 1;
  type: 'notification.created';
  workspaceId: string;
  userId: string;
  notificationId: string;
  title: string;
  body: string;
  deepLink?: string | null;
  occurredAt: string;
};

/**
 * A claim request of this workspace changed: created, re-sent or resolved.
 * Mirrors the backend's `ClaimRequestUpdatedRealtimeEvent` (DEV-782).
 */
export type ClaimRequestUpdatedRealtimeEvent = {
  v: 1;
  type: 'claim_request.updated';
  workspaceId: string;
  requestId: string;
  productId: string;
  hash: string;
  status: ClaimRequestStatus;
  method: ClaimMethod;
  expiresAt: string;
  resendCount: number;
  resolvedAt?: string | null;
  /** REJECTED only: the reason the up2promo team gave. */
  rejectionReason?: string | null;
  occurredAt: string;
};

export type RealtimeEvent =
  | ProductScannedRealtimeEvent
  | ProfileViewedRealtimeEvent
  | ReviewSyncRealtimeEvent
  | ReviewChangedRealtimeEvent
  | NotificationCreatedRealtimeEvent
  | ClaimRequestUpdatedRealtimeEvent;

function isProductScannedEvent(
  record: Record<string, unknown>,
): record is ProductScannedRealtimeEvent {
  if (record.type !== 'product.scanned') return false;
  return (
    typeof record.workspaceId === 'string' &&
    typeof record.productId === 'string' &&
    typeof record.hash === 'string' &&
    typeof record.scannedAt === 'string'
  );
}

function isProfileViewedEvent(
  record: Record<string, unknown>,
): record is ProfileViewedRealtimeEvent {
  if (record.type !== 'profile.viewed') return false;
  return (
    typeof record.workspaceId === 'string' &&
    typeof record.profileId === 'string' &&
    typeof record.source === 'string' &&
    typeof record.viewedAt === 'string'
  );
}

function isReviewSyncEvent(
  record: Record<string, unknown>,
): record is ReviewSyncRealtimeEvent {
  if (
    record.type !== 'review_sync.started' &&
    record.type !== 'review_sync.completed' &&
    record.type !== 'review_sync.failed'
  ) {
    return false;
  }
  return (
    typeof record.workspaceId === 'string' &&
    typeof record.locationId === 'string' &&
    typeof record.provider === 'string' &&
    typeof record.occurredAt === 'string'
  );
}

function isReviewChangedEvent(
  record: Record<string, unknown>,
): record is ReviewChangedRealtimeEvent {
  if (record.type !== 'review.created' && record.type !== 'review.updated') {
    return false;
  }
  return (
    typeof record.workspaceId === 'string' &&
    typeof record.locationId === 'string' &&
    typeof record.provider === 'string' &&
    typeof record.reviewId === 'string' &&
    typeof record.providerReviewId === 'string' &&
    typeof record.occurredAt === 'string'
  );
}

function isNotificationCreatedEvent(
  record: Record<string, unknown>,
): record is NotificationCreatedRealtimeEvent {
  if (record.type !== 'notification.created') return false;
  return (
    typeof record.workspaceId === 'string' &&
    typeof record.userId === 'string' &&
    typeof record.notificationId === 'string' &&
    typeof record.title === 'string' &&
    typeof record.body === 'string' &&
    typeof record.occurredAt === 'string'
  );
}

function isClaimRequestUpdatedEvent(
  record: Record<string, unknown>,
): record is ClaimRequestUpdatedRealtimeEvent {
  if (record.type !== 'claim_request.updated') return false;
  return (
    typeof record.workspaceId === 'string' &&
    typeof record.requestId === 'string' &&
    typeof record.productId === 'string' &&
    typeof record.hash === 'string' &&
    Object.values<unknown>(ClaimRequestStatus).includes(record.status) &&
    Object.values<unknown>(ClaimMethod).includes(record.method) &&
    typeof record.expiresAt === 'string' &&
    typeof record.resendCount === 'number' &&
    (record.rejectionReason == null ||
      typeof record.rejectionReason === 'string') &&
    typeof record.occurredAt === 'string'
  );
}

export type ProductScan = {
  productId: string;
  hash: string;
  label: string | null;
  scannedAt: string;
};

/** The scan carried by a `product.scanned` event; null for other events. */
export function toProductScan(event: RealtimeEvent): ProductScan | null {
  if (event.type === 'product.scanned') {
    return {
      productId: event.productId,
      hash: event.hash,
      label: event.label ?? null,
      scannedAt: event.scannedAt,
    };
  }
  return null;
}

export function isRealtimeEvent(value: unknown): value is RealtimeEvent {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  if (record.v !== 1) return false;
  return (
    isProductScannedEvent(record) ||
    isProfileViewedEvent(record) ||
    isReviewSyncEvent(record) ||
    isReviewChangedEvent(record) ||
    isNotificationCreatedEvent(record) ||
    isClaimRequestUpdatedEvent(record)
  );
}

export function parseRealtimeEventPayload(
  payload: string,
): RealtimeEvent | null {
  try {
    const parsed: unknown = JSON.parse(payload);
    return isRealtimeEvent(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
